import {
	type MouseEvent as ReactMouseEvent,
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { useTranslation } from '@/translations/i18n'
import {
	CATEGORY_COLUMN_WIDTH,
	MIN_DAY_WIDTH,
	PAN_CLICK_SLOP
} from '../constants'
import type { RoadmapActivity, RoadmapConfig } from '../types'
import {
	buildColumns,
	buildColumnUtilization,
	buildMonths,
	buildTimeline,
	packIntoLanes,
	positionActivity,
	positionRange,
	timelineTotalUnits,
	todayUnitIndex
} from '../utils'
import { GanttCategoryRow } from './gantt-category-row'
import { GanttTimelineHeader } from './gantt-timeline-header'

interface GanttChartProps {
	config: RoadmapConfig
	/** Opens an activity for editing. Omitted for read-only viewers. */
	onActivitySelect?: (activity: RoadmapActivity) => void
}

function GanttChart({ config, onActivitySelect }: GanttChartProps) {
	const { t } = useTranslation()
	const scrollRef = useRef<HTMLDivElement | null>(null)
	const observerRef = useRef<ResizeObserver | null>(null)
	const [containerWidth, setContainerWidth] = useState(0)

	// The axis every measure below is expressed in: working days only.
	const timeline = useMemo(
		() => buildTimeline(config.dateFrom, config.dateTo),
		[config.dateFrom, config.dateTo]
	)

	/**
	 * Measured rather than assumed: the chart has to fill whatever width the page
	 * gives it, which nothing here knows up front. A callback ref rather than a
	 * mount effect, so it re-binds to whichever node is currently mounted.
	 */
	const setScrollNode = useCallback((node: HTMLDivElement | null) => {
		observerRef.current?.disconnect()
		scrollRef.current = node

		if (!node) {
			return
		}

		// Measure now as well as on resize: ResizeObserver's first callback is
		// async, which would paint one frame at MIN_DAY_WIDTH before snapping to
		// full width.
		setContainerWidth(node.clientWidth)

		const observer = new ResizeObserver(([entry]) => {
			setContainerWidth(entry.contentRect.width)
		})
		observer.observe(node)
		observerRef.current = observer
	}, [])

	const totalUnits = timelineTotalUnits(timeline)
	/**
	 * Stretch to fill the container, but never below MIN_DAY_WIDTH: past that
	 * the chart scrolls instead of squashing. Zero on first paint, before the
	 * observer has measured, which falls back to the minimum.
	 */
	const availableWidth = Math.max(containerWidth - CATEGORY_COLUMN_WIDTH, 0)
	const dayWidth = Math.max(MIN_DAY_WIDTH, availableWidth / totalUnits)
	const timelineWidth = totalUnits * dayWidth

	const columns = useMemo(() => buildColumns(timeline), [timeline])
	const months = useMemo(() => buildMonths(timeline), [timeline])

	/** Position and lane-pack each category's activities. */
	const rows = useMemo(
		() =>
			config.categories.map((category) => ({
				category,
				lanes: packIntoLanes(
					category.activities.flatMap((activity) => {
						const positioned = positionActivity(activity, timeline)
						return positioned ? [positioned] : []
					})
				)
			})),
		[config.categories, timeline]
	)

	/** Utilization per week, over the bars actually on the chart. */
	const columnUtilization = useMemo(
		() =>
			buildColumnUtilization(
				columns,
				rows.flatMap((row) => row.lanes.flat()),
				timeline
			),
		[columns, rows, timeline]
	)

	/**
	 * Null when today falls outside the roadmap window: a past year gets no
	 * marker rather than one pinned to an edge, which would lie about where we
	 * are.
	 */
	const todayOffsetUnits = useMemo(() => todayUnitIndex(timeline), [timeline])

	/**
	 * Open on today rather than on January. A year is far wider than the
	 * viewport, and the far past is the least useful thing to land on.
	 *
	 * Once only, hence the ref: this sets scrollLeft, so re-running it would
	 * yank the view back every time the container resized or the user had
	 * scrolled somewhere deliberately.
	 */
	const hasScrolledToTodayRef = useRef(false)

	useEffect(() => {
		const container = scrollRef.current

		if (
			!container ||
			hasScrolledToTodayRef.current ||
			todayOffsetUnits === null ||
			// dayWidth is still the fallback until the first measurement, which
			// would put the scroll in the wrong place.
			containerWidth === 0
		) {
			return
		}

		hasScrolledToTodayRef.current = true
		// A third in, so the weeks just gone stay in view and the road ahead gets
		// the rest.
		container.scrollLeft = Math.max(
			CATEGORY_COLUMN_WIDTH +
				todayOffsetUnits * dayWidth -
				containerWidth / 3,
			0
		)
	}, [todayOffsetUnits, dayWidth, containerWidth])

	const overlays = useMemo(
		() =>
			config.overlays.flatMap((overlay) => {
				const positioned = positionRange(overlay, timeline)
				return positioned ? [{ overlay, ...positioned }] : []
			}),
		[config.overlays, timeline]
	)

	/**
	 * Set when a pan actually moved, so the click that ends the gesture does
	 * not also open the bar it happened to start on. Dragging the chart by
	 * grabbing a bar is the natural gesture, and it must not count as a click.
	 */
	const hasPannedRef = useRef(false)

	/** Drag-to-pan, on the whole chart surface including the bars. */
	const handlePanStart = (event: ReactPointerEvent<HTMLDivElement>) => {
		const container = scrollRef.current
		if (!container || event.button !== 0) {
			return
		}

		const startX = event.clientX
		const startScrollLeft = container.scrollLeft
		hasPannedRef.current = false

		const handleMove = (moveEvent: PointerEvent) => {
			// A few pixels of slop: a plain click rarely lands on the exact
			// same pixel it started from.
			if (Math.abs(moveEvent.clientX - startX) > PAN_CLICK_SLOP) {
				hasPannedRef.current = true
			}
			container.scrollLeft =
				startScrollLeft - (moveEvent.clientX - startX)
		}
		const handleEnd = () => {
			window.removeEventListener('pointermove', handleMove)
			window.removeEventListener('pointerup', handleEnd)
		}

		window.addEventListener('pointermove', handleMove)
		window.addEventListener('pointerup', handleEnd)
	}

	/** Swallows the click that closes a pan, before it reaches a bar. */
	const handleClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
		if (hasPannedRef.current) {
			event.stopPropagation()
			event.preventDefault()
			hasPannedRef.current = false
		}
	}

	return (
		<div
			className='scrollbar overflow-auto rounded-xl border border-neutral-200 bg-neutral-white'
			ref={setScrollNode}
		>
			<div style={{ width: CATEGORY_COLUMN_WIDTH + timelineWidth }}>
				<GanttTimelineHeader
					columns={columns}
					dayWidth={dayWidth}
					months={months}
					timelineWidth={timelineWidth}
					utilization={columnUtilization}
				/>

				{/* Pan surface. Not keyboard-reachable on purpose: the scroll
				    container it drives is natively scrollable. */}
				<div
					className='relative cursor-grab active:cursor-grabbing'
					onClickCapture={handleClickCapture}
					onPointerDown={handlePanStart}
				>
					{/* Backdrop layers: overlays and grid lines sit behind the bars
					    and never take pointer events. */}
					<div
						className='pointer-events-none absolute inset-y-0'
						style={{
							left: CATEGORY_COLUMN_WIDTH,
							width: timelineWidth
						}}
					>
						{/* The week padding: real days, greyed out because they fall
						    outside the roadmap. They are here so the first and last
						    week read as weeks. */}
						{timeline.firstEnabledIndex > 0 ? (
							<div
								className='absolute inset-y-0 left-0 bg-neutral-100'
								style={{
									width: timeline.firstEnabledIndex * dayWidth
								}}
							/>
						) : null}
						{timeline.lastEnabledIndex < totalUnits - 1 ? (
							<div
								className='absolute inset-y-0 bg-neutral-100'
								style={{
									left:
										(timeline.lastEnabledIndex + 1) *
										dayWidth,
									width:
										(totalUnits -
											1 -
											timeline.lastEnabledIndex) *
										dayWidth
								}}
							/>
						) : null}

						{overlays.map(({ overlay, offsetUnits, spanUnits }) => (
							<div
								className='absolute inset-y-0 border-neutral-300 border-x border-dashed bg-neutral-50'
								key={overlay.id}
								style={{
									left: offsetUnits * dayWidth,
									width: spanUnits * dayWidth
								}}
							>
								<span className='absolute inset-x-0 top-1 truncate px-1 text-center font-medium text-neutral-600 text-xs'>
									{overlay.title}
								</span>
							</div>
						))}

						{columns.map((column) => (
							<div
								className='absolute inset-y-0 border-neutral-200 border-l border-dashed'
								key={column.key}
								style={{ left: column.offsetUnits * dayWidth }}
							/>
						))}
					</div>

					{rows.map(({ category, lanes }) => (
						<GanttCategoryRow
							dayWidth={dayWidth}
							key={category.id}
							lanes={lanes}
							onActivitySelect={onActivitySelect}
							timelineWidth={timelineWidth}
							title={category.title}
						/>
					))}

					{/* Rendered after the rows so it reads on top of the bars it
					    crosses, and left of the sticky category column so it slides
					    under the labels rather than over them. */}
					{todayOffsetUnits === null ? null : (
						<div
							aria-hidden='true'
							className='pointer-events-none absolute inset-y-0 w-0 border-primary-600 border-l-2'
							style={{
								left:
									CATEGORY_COLUMN_WIDTH +
									todayOffsetUnits * dayWidth
							}}
						>
							<span className='absolute top-0 -translate-x-1/2 rounded-b-sm bg-primary-600 px-1 font-medium text-neutral-white text-xs'>
								{t('roadmaps:today')}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export { GanttChart }
